import { ThemeColor } from '@/enums'
import { SearchIcon } from '@/icons'
import { useInsuranceGroups } from '@/modules/insurances/hooks/useInsuranceGroups'
import { fromEventTarget, includesIgnoringCase, useItemDialog } from '@/utils'
import { uniq } from 'ramda'
import { Fragment, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InputWithAddon, Option, Typography } from '../../atoms'
import { BasicFormItem, ListSelect, ModalBase } from '../../molecules'
import { Column, Divider } from '../../styled'
import { SearchContainer } from '../AddInsuranceView/styles'
import { CardNode } from './CardNode'
import { SelectedCardNode } from './SelectedCardNode'
import {
  Container,
  ContentWrapper,
  EmptySearchContainer,
  HeaderContainer,
  InsuranceGroup,
  SelectedInsurancesGroup,
} from './styles'
import { SelectOptionValueType } from '@/components/molecules/ListSelect/OptionNode'

interface CardItem {
  title: string
  subtitle?: string
  value: string | number
}

interface CardOption {
  groupName: React.ReactNode
  items: CardItem[]
}

interface ListOption {
  text: string
  value: string | number
  icon?: string
}

interface ValueItem {
  [key: string]: string | number | SelectOptionValueType
}

interface GroupCardSelectWithModalProps {
  value?: ValueItem[]
  onChange: (value: ValueItem[]) => void
  resetText?: string
  cardOptions: CardOption[]
  listOptions: ListOption[]
  listKey: string
  cardKey: string
  searchPlaceholder?: string
  modalTitle?: string
  modalSubtitle?: string
  name?: string
}

interface EmptySearchStateProps {
  search: string
}

export const GroupCardSelectWithModal: React.FC<GroupCardSelectWithModalProps> = ({
  value = [],
  onChange,
  resetText,
  cardOptions,
  listOptions,
  listKey,
  cardKey,
  searchPlaceholder,
  modalTitle,
  modalSubtitle,
}) => {
  const {
    item: dialogItem,
    openItemDialog,
    isOpen,
    closeItemDialog,
  } = useItemDialog<{ value: string | number; text: string }>({
    value: '',
    text: '',
  })

  const { t } = useTranslation()

  const [search, setSearch] = useState('')

  const isSelected = (selectedCardValue: string | number): boolean =>
    !!value.find((item) => item[cardKey] === selectedCardValue)

  const onDelete = (selectedCardValue: string | number): void =>
    onChange(value.filter((item) => item[cardKey] !== selectedCardValue))

  const findListItem = (selectedCardValue: string | number): ListOption => {
    const { [listKey]: listValue } =
      value.find((item) => item[cardKey] === selectedCardValue) || {}

    return listOptions.find((item) => item.value === listValue)!
  }

  const onListChange = (optionValue: SelectOptionValueType): void => {
    onChange([
      ...value.filter((item) => item[cardKey] !== dialogItem.value),
      { [listKey]: optionValue, [cardKey]: dialogItem.value } as ValueItem,
    ])
    setSearch('')

    closeItemDialog()
  }

  const selected = useMemo(() => {
    const options = cardOptions
      .flatMap(({ items }) => items)
      .filter(({ value: option }) => isSelected(option))

    return uniq(options)
  }, [value])

  const insuranceGroups = useInsuranceGroups()

  const filteredOptions = useMemo(
    () =>
      insuranceGroups
        .map((group) => ({
          ...group,
          items: group.items.filter(
            (item) =>
              includesIgnoringCase(search, item.title) &&
              !isSelected(item.value),
          ),
        }))
        .filter((group) => group.items.length),
    [JSON.stringify(cardOptions), search],
  )

  return (
    <Container>
      <ContentWrapper>
        <SearchContainer>
          <InputWithAddon
            addonBefore={() => <SearchIcon color={ThemeColor.b50} />}
            onChange={fromEventTarget(setSearch)}
            placeholder={t('yearlyAssessment.searchCoverage')}
            rounded
            value={search}
          />
          <Divider color={ThemeColor.b30} />
        </SearchContainer>
        {!search && selected.length ? (
          <SelectedInsurancesGroup>
            <>
              <Typography bold color={ThemeColor.b50} variant="badgeText">
                {t('selected')}
              </Typography>
              {selected.map(({ title, subtitle, value: selectedValue }) => (
                <SelectedCardNode
                  key={selectedValue}
                  listItem={findListItem(selectedValue)}
                  onDelete={onDelete}
                  onResetClick={openItemDialog}
                  resetText={resetText || ''}
                  subtext={subtitle || ''}
                  text={title}
                  value={selectedValue}
                />
              ))}
            </>
          </SelectedInsurancesGroup>
        ) : null}
        {filteredOptions.length ? (
          filteredOptions?.map(({ groupName, items }) => (
            <InsuranceGroup key={groupName as string}>
              <Typography bold color={ThemeColor.b50} variant="badgeText">
                {groupName}
              </Typography>
              {items?.map(({ title, subtitle, value: selectedValue }) => (
                <Fragment key={selectedValue}>
                  {isSelected(selectedValue) ? null : (
                    <CardNode
                      onClick={openItemDialog}
                      subtext={subtitle}
                      text={title}
                      value={selectedValue}
                    />
                  )}
                </Fragment>
              ))}
            </InsuranceGroup>
          ))
        ) : (
          <EmptySearchState search={search} />
        )}
        {isOpen && (
          <ModalBase
            closable
            enableClickOutside={false}
            isOpen={isOpen}
            onClose={closeItemDialog}
            shouldRenderInPortal
          >
            <HeaderContainer>
              <Typography color={ThemeColor.b50} variant="p1Body">
                {dialogItem.text}
              </Typography>
              <Typography bold variant="h4">
                {modalTitle}
              </Typography>
              <Typography color={ThemeColor.b50} variant="p1Body">
                {modalSubtitle}
              </Typography>
            </HeaderContainer>
            <ListSelect
              onChange={onListChange}
              searchPlaceholder={searchPlaceholder}
              value={
                value.find((item) => item[cardKey] === dialogItem.value)?.[
                  listKey
                ]
              }
            >
              {listOptions.map(({ text, value: optionValue, icon }) => (
                <Option key={optionValue} icon={icon} value={optionValue}>
                  {text}
                </Option>
              ))}
            </ListSelect>
          </ModalBase>
        )}
      </ContentWrapper>
    </Container>
  )
}

export const FormGroupCardWithSelectModal: React.FC<GroupCardSelectWithModalProps> = (
  props,
) => (
  <BasicFormItem
    name={props.cardKey}
    component={GroupCardSelectWithModal}
    {...props}
  />
)

export const EmptySearchState: React.FC<EmptySearchStateProps> = ({
  search,
}) => {
  const { t } = useTranslation()
  return (
    <EmptySearchContainer>
      <SearchIcon size="button" />
      <Column gap="4px">
        <Typography bold variant="p1Body">
          {t('yearlyAssessment.noInsuranceFound')}
        </Typography>
        <Typography color={ThemeColor.b50} variant="p1Body">
          {t('yearlyAssessment.noInsuranceFoundDescription', { search })}
        </Typography>
      </Column>
    </EmptySearchContainer>
  )
}
